import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, Subject } from 'rxjs';
import { API_PREFIX, resolveUrl } from 'src/core/constants/api.constants';
import { spreadDelayMs } from 'src/core/helpers/jitter.helper';
import { CampaignProgressDto } from 'src/core/models/campaign/campaign-progress.dto';
import { ScheduledCallOfferEndedEvent } from 'src/app/shared/agent-desktop/scheduled-call/models/scheduled-call-offer-ended-event.model';
import { ScheduledCallOfferEndedReason } from 'src/app/shared/agent-desktop/scheduled-call/models/scheduled-call-offer-ended-reason.enum';
import { HubService } from './hub.service';
import { RETRY_DELAY_SPREAD_RATIO } from './jittered-retry-policy';

const AGENT_HEARTBEAT_HUB_URL = resolveUrl('/api/hubs/agent-heartbeat');
const AGENT_HEARTBEAT_UNREGISTER_URL = resolveUrl(`${API_PREFIX}/agent-heartbeat/unregister`);
const RESTART_DELAYS_MS = [2000, 5000, 10000, 30000];

@Injectable({ providedIn: 'root' })
export class AgentHeartbeatService extends HubService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  private readonly oauthService = inject(OAuthService);
  private readonly unloadEvents = ['pagehide', 'beforeunload'] as const;
  private readonly pageExitHandler = () => this.handlePageExit();
  private readonly agentStatusChanged = new Subject<number>();
  private readonly hubReconnectedSource = new Subject<void>();
  private readonly campaignProgressChanged = new Subject<CampaignProgressDto>();
  private readonly campaignPoolExhausted = new Subject<number>();
  private readonly scheduledCallOfferEnded = new Subject<ScheduledCallOfferEndedEvent>();
  private readonly scheduledCallDue = new Subject<void>();
  private readonly myScheduledCallsChanged = new Subject<void>();
  private sessionId: number | null = null;
  /** Last reported callback readiness (K-264); the server's Register resets it to not ready, so `true` is sent again. */
  private scheduledCallReadiness = false;
  private isPageExitInProgress = false;
  private arePageExitHandlersRegistered = false;
  private joinedCampaignId: number | null = null;
  private restartTimer: ReturnType<typeof setTimeout> | null = null;
  private restartAttempt = 0;

  /** Emits the new statusId whenever the agent's status changes (broadcast to all tabs). */
  readonly agentStatusChanged$: Observable<number> = this.agentStatusChanged.asObservable();
  /** Emits after the hub successfully reconnects and re-registers. */
  readonly hubReconnected$: Observable<void> = this.hubReconnectedSource.asObservable();
  /** Emits the latest real-time progress snapshot for a campaign the agent is running. */
  readonly campaignProgressChanged$: Observable<CampaignProgressDto> =
    this.campaignProgressChanged.asObservable();
  /** Emits the campaign id whenever a campaign's reservable contact pool becomes exhausted. */
  readonly campaignPoolExhausted$: Observable<number> = this.campaignPoolExhausted.asObservable();
  /** Emits when the agent's callback offer ended or started in another tab; details come from `GetMyOffer`. */
  readonly scheduledCallOfferEnded$: Observable<ScheduledCallOfferEndedEvent> =
    this.scheduledCallOfferEnded.asObservable();
  /** Emits when the server sees a due callback this agent may take (K-264); the offer itself comes from `ClaimNext`. */
  readonly scheduledCallDue$: Observable<void> = this.scheduledCallDue.asObservable();
  /** Emits when the agent's own upcoming callback list changed on the server (K-264); the list comes from `GetMyUpcoming`. */
  readonly myScheduledCallsChanged$: Observable<void> = this.myScheduledCallsChanged.asObservable();

  constructor() {
    super();
    this.initialize({ url: AGENT_HEARTBEAT_HUB_URL });
    this.destroyRef.onDestroy(() => this.unregisterPageExitHandlers());
  }

  async connect(sessionId: number): Promise<void> {
    this.registerPageExitHandlers();
    this.sessionId = sessionId;
    this.isPageExitInProgress = false;

    if (this.isConnected()) {
      await this.register();
      return;
    }

    try {
      await this.startConnection();
    } catch (error) {
      // A connection that never started gets no onclose, so the restart chain is armed here.
      console.error('[AgentHeartbeatService] Hub start failed; retrying', error);
      this.onClosed();
    }
  }

  async disconnect(): Promise<void> {
    this.sessionId = null;
    this.isPageExitInProgress = false;
    this.cancelRestart();
    await this.stopConnection('Unregister');
    this.unregisterPageExitHandlers();
  }

  protected override async onConnected(): Promise<void> {
    this.hubConnection?.off('OnAgentStatusChanged');
    this.hubConnection?.on('OnAgentStatusChanged', (statusId: number) => {
      this.agentStatusChanged.next(statusId);
    });
    this.hubConnection?.off('OnCampaignProgressChanged');
    this.hubConnection?.on('OnCampaignProgressChanged', (progress: CampaignProgressDto) => {
      this.campaignProgressChanged.next(progress);
    });
    this.hubConnection?.off('OnCampaignContactPoolExhausted');
    this.hubConnection?.on('OnCampaignContactPoolExhausted', (campaignId: number) => {
      this.campaignPoolExhausted.next(campaignId);
    });
    this.hubConnection?.off('OnScheduledCallOfferEnded');
    this.hubConnection?.on(
      'OnScheduledCallOfferEnded',
      (attemptId: number, reason: ScheduledCallOfferEndedReason) => {
        this.scheduledCallOfferEnded.next({ attemptId, reason });
      },
    );
    this.hubConnection?.off('OnScheduledCallDue');
    this.hubConnection?.on('OnScheduledCallDue', () => this.scheduledCallDue.next());
    this.hubConnection?.off('OnMyScheduledCallsChanged');
    this.hubConnection?.on('OnMyScheduledCallsChanged', () => this.myScheduledCallsChanged.next());
    await this.register();
  }

  protected override async onReconnected(): Promise<void> {
    await this.register();
    if (this.joinedCampaignId != null) {
      void this.invoke('JoinCampaignGroup', this.joinedCampaignId);
    }
    this.hubReconnectedSource.next();
  }

  /** H3-01: automatic reconnect gave up while the session lives; start over so status pushes resume. */
  protected override onClosed(): void {
    if (this.sessionId == null || this.restartTimer !== null) {
      return;
    }

    const baseDelay = RESTART_DELAYS_MS[Math.min(this.restartAttempt, RESTART_DELAYS_MS.length - 1)];
    const delay = spreadDelayMs(this.randomFraction, baseDelay, RETRY_DELAY_SPREAD_RATIO);
    this.restartAttempt++;
    this.zone.runOutsideAngular(() => {
      this.restartTimer = setTimeout(() => {
        this.restartTimer = null;
        this.zone.run(() => void this.restartConnection());
      }, delay);
    });
  }

  /**
   * Reports whether this tab may take a callback offer now (K-264). Only the latest value is kept: it is sent when it
   * changes on a registered connection and, if `true`, again after every Register; nothing is queued while disconnected.
   * @param isReady The offer service's `canClaim`.
   */
  setScheduledCallReadiness(isReady: boolean): void {
    if (isReady === this.scheduledCallReadiness) {
      return;
    }

    this.scheduledCallReadiness = isReady;
    this.sendScheduledCallReadiness();
  }

  setActiveSegment(segmentId: number): void {
    void this.invoke('SetActiveSegment', segmentId);
  }

  /**
   * Joins the per-campaign SignalR group so this connection receives that campaign's real-time
   * progress/pool-exhausted pushes. The group is remembered and re-joined after a reconnect.
   * @param campaignId Identifier of the campaign whose group to join.
   */
  joinCampaignGroup(campaignId: number): void {
    this.joinedCampaignId = campaignId;
    void this.invoke('JoinCampaignGroup', campaignId);
  }

  /**
   * Leaves the per-campaign SignalR group and clears the remembered campaign so a reconnect no longer
   * re-joins it.
   * @param campaignId Identifier of the campaign whose group to leave.
   */
  leaveCampaignGroup(campaignId: number): void {
    this.joinedCampaignId = null;
    void this.invoke('LeaveCampaignGroup', campaignId);
  }

  private async restartConnection(): Promise<void> {
    if (this.sessionId == null) {
      return;
    }

    try {
      this.abandonConnection();
      await this.startConnection();
    } catch {
      this.onClosed();
      return;
    }

    if (this.sessionId == null) {
      await this.stopConnection();
      return;
    }

    this.restartAttempt = 0;
    if (this.joinedCampaignId != null) {
      void this.invoke('JoinCampaignGroup', this.joinedCampaignId);
    }
    this.hubReconnectedSource.next();
  }

  private cancelRestart(): void {
    if (this.restartTimer !== null) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }
    this.restartAttempt = 0;
  }

  private async register(): Promise<void> {
    if (this.sessionId == null) {
      return;
    }

    await this.invoke('Register', this.sessionId);
    if (this.scheduledCallReadiness) {
      this.sendScheduledCallReadiness();
    }
  }

  private sendScheduledCallReadiness(): void {
    this.invoke('SetScheduledCallReadiness', this.scheduledCallReadiness).catch((error: unknown) =>
      console.error('[AgentHeartbeatService] SetScheduledCallReadiness failed', error),
    );
  }

  private registerPageExitHandlers(): void {
    if (typeof window === 'undefined' || this.arePageExitHandlersRegistered) {
      return;
    }

    for (const eventName of this.unloadEvents) {
      window.addEventListener(eventName, this.pageExitHandler);
    }

    this.arePageExitHandlersRegistered = true;
  }

  private unregisterPageExitHandlers(): void {
    if (typeof window === 'undefined' || !this.arePageExitHandlersRegistered) {
      return;
    }

    for (const eventName of this.unloadEvents) {
      window.removeEventListener(eventName, this.pageExitHandler);
    }

    this.arePageExitHandlersRegistered = false;
  }

  private handlePageExit(): void {
    if (this.isPageExitInProgress || this.sessionId == null) {
      return;
    }

    const connectionId = this.getConnectionId();
    if (!connectionId) {
      return;
    }

    this.isPageExitInProgress = true;
    this.sessionId = null;
    this.sendUnloadRequest(connectionId);
    this.abandonConnection();
  }

  private sendUnloadRequest(connectionId: string): void {
    const accessToken = this.oauthService.getAccessToken();
    if (!accessToken || typeof fetch === 'undefined') {
      return;
    }

    const request: RequestInit = {
      method: 'POST',
      keepalive: true,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ connectionId }),
    };

    try {
      void fetch(AGENT_HEARTBEAT_UNREGISTER_URL, request);
    } catch {
      // Ignore unload-time transport errors.
    }
  }
}
